// API layer for per-user preferences (dashboard clocks, notifications, appearance).
// Mirrors checkInFormConfig.js: axios (base URL already includes /api/v1), responses under res.data.data,
// localStorage as an offline cache + fallback. The backend stores preferences per authenticated user, so
// these settings follow the user across browsers/devices.

import axios from "const/axios";

const APPEARANCE_CACHE_KEY = "pref.appearance";
const NOTIFICATIONS_CACHE_KEY = "pref.notifications";

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — cache simply skipped */
  }
};

let fetchAllPreferencesInFlight = null;
let fetchAllPreferencesCache = null;

/** Fetch all preference areas in one call: { clocks, notifications, appearance }. */
export const fetchAllPreferences = async ({ force = false } = {}) => {
  if (!force && fetchAllPreferencesCache) {
    return fetchAllPreferencesCache;
  }
  if (!force && fetchAllPreferencesInFlight) {
    return fetchAllPreferencesInFlight;
  }

  fetchAllPreferencesInFlight = (async () => {
    try {
      const res = await axios.get("/preferences");
      const data = res?.data?.data ?? {};
      fetchAllPreferencesCache = data;
      return data;
    } finally {
      fetchAllPreferencesInFlight = null;
    }
  })();

  return fetchAllPreferencesInFlight;
};

export const clearPreferencesCache = () => {
  fetchAllPreferencesCache = null;
  fetchAllPreferencesInFlight = null;
};

/* ---------------- Appearance ---------------- */

export const fetchAppearancePreference = async () => {
  try {
    const res = await axios.get("/preferences/appearance");
    const data = res?.data?.data ?? null;
    if (data) writeCache(APPEARANCE_CACHE_KEY, data);
    return data;
  } catch {
    return readCache(APPEARANCE_CACHE_KEY);
  }
};

export const saveAppearancePreference = async (appearance) => {
  writeCache(APPEARANCE_CACHE_KEY, appearance);
  await axios.put("/preferences/appearance", appearance);
  if (fetchAllPreferencesCache) {
    fetchAllPreferencesCache = { ...fetchAllPreferencesCache, appearance };
  }
};

/* ---------------- Notifications ---------------- */

export const fetchNotificationPreference = async () => {
  try {
    const res = await axios.get("/preferences/notifications");
    const data = res?.data?.data ?? null;
    if (data) writeCache(NOTIFICATIONS_CACHE_KEY, data);
    return data;
  } catch {
    return readCache(NOTIFICATIONS_CACHE_KEY);
  }
};

export const saveNotificationPreference = async (notifications) => {
  writeCache(NOTIFICATIONS_CACHE_KEY, notifications);
  await axios.put("/preferences/notifications", notifications);
  if (fetchAllPreferencesCache) {
    fetchAllPreferencesCache = { ...fetchAllPreferencesCache, notifications };
  }
};

/* ---------------- Clocks ---------------- */

// clocks: [{ label, timeZone, enabled }] in display order (no id — order is the position).
export const fetchClockPreference = async () => {
  const res = await axios.get("/preferences/clocks");
  return res?.data?.data ?? null;
};

export const saveClockPreference = async (clocks) => {
  await axios.put("/preferences/clocks", { clocks });
  if (fetchAllPreferencesCache) {
    fetchAllPreferencesCache = { ...fetchAllPreferencesCache, clocks };
  }
};
